import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import Body2 from '@joinforma/ui/components/Body2';
import Menu from '@joinforma/ui/components/Menu';
import MenuLabel from '@joinforma/ui/components/MenuLabel';
import MenuItem from '@joinforma/ui/components/MenuItem';
import { ReactComponent as UserSvg } from '@joinforma/ui/assets/icons/user.svg';
import { ReactComponent as UserPlusSvg } from '@joinforma/ui/assets/icons/user-plus.svg';
import { ReactComponent as LinkSvg } from '@joinforma/ui/assets/icons/link.svg';
import { ReactComponent as DashboardSvg } from '@joinforma/ui/assets/icons/dashboard.svg';
import { ReactComponent as CreditCardSvg } from '@joinforma/ui/assets/icons/credit-card.svg';
import { ReactComponent as NotificationSvg } from '@joinforma/ui/assets/icons/custom-attributes.svg';
import { ReactComponent as LockSvg } from '@joinforma/ui/assets/icons/lock.svg';
import { ReactComponent as CaretDownSvg } from '@joinforma/ui/assets/icons/caret-down.svg';
import { ReactComponent as CaretUpSvg } from '@joinforma/ui/assets/icons/caret-up.svg';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import SideNavigationBar from 'components/common/SideNavigationBar';
import SideLink from 'components/common/SideLink';
import Backlink from 'components/common/Backlink';

import { SETTINGS_PAGE_ELEMENTS } from 'constants/testSelectors';
import { SettingsRoutes } from 'utils/routeGenerator';
import { SvgIconSize, SvgIconColor } from '@joinforma/ui/constants/properties';

import { Employee, Company } from './types';
import { displayFormaCards } from 'utils/formaCards';
import { CardTabName } from 'constants/unit';

const Container = styled.div.attrs({ role: 'navigation' })``;

const MobileContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  display: block;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: none;
  }
`;

const DesktopContainer = styled.div`
  width: 202px;
  display: none;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: flex;
    flex-direction: column;
  }
`;

const AccountContainer = styled.div``;

const StyledNotificationIcon = styled(SvgIcon).attrs({
  icon: NotificationSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const StyledUserIcon = styled(SvgIcon).attrs({
  icon: UserSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const StyledUserPlusIcon = styled(SvgIcon).attrs({
  icon: UserPlusSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const StyledBankIcon = styled(SvgIcon).attrs({
  icon: LinkSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const StyledDashboardIcon = styled(SvgIcon).attrs({
  icon: DashboardSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const StyledCreditCardIcon = styled(SvgIcon).attrs({
  icon: CreditCardSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const StyledLockIcon = styled(SvgIcon).attrs({
  icon: LockSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.PRIMARY,
})``;

const NavigationDropdown = styled.button`
  cursor: pointer;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: none;
  border: ${({
    theme: {
      colors: {
        background: { stroke },
      },
    },
  }) => `1px solid ${stroke}`};
`;

const CaretDownIcon = styled(SvgIcon).attrs({
  icon: CaretDownSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.INTERACTIVE,
})``;

const CaretUpIcon = styled(SvgIcon).attrs({
  icon: CaretUpSvg,
  size: SvgIconSize.MEDIUM,
  fill: SvgIconColor.INTERACTIVE,
})``;

const StyledMenu = styled(Menu)`
  width: 100%;
`;

const ActiveTabText = styled(Body2)`
  color: ${({
    theme: {
      colors: {
        text: { link },
      },
    },
  }) => link};
`;

const renderActiveTabText = (active_path: string) => {
  switch (active_path) {
    case SettingsRoutes.profile():
      return 'Profile';
    case SettingsRoutes.payment():
    case SettingsRoutes.bankAccounts():
      return 'Linked accounts';
    case SettingsRoutes.notifications():
      return 'Notifications';
    case SettingsRoutes.integrations():
      return 'Integrations';
    case SettingsRoutes.security():
      return 'Security';
    case SettingsRoutes.pretaxCardList():
    case SettingsRoutes.pretaxCardNew():
      return 'Benefits Card';
    case SettingsRoutes.posttaxCardList():
    case SettingsRoutes.posttaxCardNew():
    case SettingsRoutes.posttaxCardEdit():
      return 'Forma Card';
    default:
      return 'Settings';
  }
};

const SettingsFilter = ({
  active_path,
  company,
  employee,
  show_dependent,
  is_unit_forma_card_enabled,
  is_stripe_card_disabled,
  hasAlegeus,
  hasAlegeusHsaAccount,
  has_pretax_v2_accounts,
}: {
  active_path: string;
  company: Company;
  employee: Employee;
  show_dependent?: boolean;
  is_unit_forma_card_enabled: boolean;
  is_stripe_card_disabled: boolean;
  hasAlegeusHsaAccount: boolean;
  hasAlegeus: boolean;
  has_pretax_v2_accounts: boolean;
}) => {
  const [is_open, setIsOpen] = useState(false);
  const location = useLocation();
  const {
    settings: { slack, sso },
    features: { credit_card },
  } = company;
  const {
    settings: { country, is_posttax_enabled },
    employee_wallets,
  } = employee;

  const isAmerican = country === 'us';
  const showIntegration = !!slack;
  const showSecurity = !sso;

  const has_posttax_wallets =
    credit_card &&
    is_posttax_enabled &&
    !is_stripe_card_disabled &&
    employee_wallets.length;

  const showPostTaxCards = Boolean(isAmerican && has_posttax_wallets);

  const showFormaCards = displayFormaCards({
    has_pretax_v2_accounts,
    is_unit_forma_card_enabled,
    is_credit_card_enabled: credit_card,
  });

  return (
    <Container data-testid={SETTINGS_PAGE_ELEMENTS.SIDE_NAV_LINK}>
      <MobileContainer>
        <Backlink />
        <NavigationDropdown onClick={() => setIsOpen(true)}>
          <ActiveTabText>{renderActiveTabText(active_path)}</ActiveTabText>
          {is_open ? <CaretUpIcon /> : <CaretDownIcon />}
        </NavigationDropdown>
        <StyledMenu open={is_open} onClose={() => setIsOpen(false)}>
          <MenuLabel>Forma</MenuLabel>
          <MenuItem
            startText="Profile"
            startIcon={UserSvg}
            to={SettingsRoutes.profile()}
          />
          {show_dependent && (
            <MenuItem
              startText="Dependents"
              startIcon={UserPlusSvg}
              to={SettingsRoutes.dependents()}
            />
          )}
          <MenuItem
            startText="Notifications"
            startIcon={NotificationSvg}
            to={SettingsRoutes.notifications()}
          />
          <MenuItem
            startText="Linked accounts"
            startIcon={LinkSvg}
            to={SettingsRoutes.bankAccounts()}
          />
          {showIntegration && (
            <MenuItem
              startText="Integrations"
              startIcon={DashboardSvg}
              to={SettingsRoutes.integrations()}
            />
          )}
          {showFormaCards && (
            <MenuItem
              startText={CardTabName.PRETAX_FORMA_CARD}
              startIcon={CreditCardSvg}
              to={SettingsRoutes.formaCard()}
            />
          )}
          {showPostTaxCards && (
            <MenuItem
              startText={CardTabName.FORMA_CARD}
              startIcon={CreditCardSvg}
              to={SettingsRoutes.posttaxCardList()}
            />
          )}
          {hasAlegeus && (
            <MenuItem
              startText={
                hasAlegeusHsaAccount
                  ? CardTabName.WCS_HSA_CARD
                  : CardTabName.BENEFITS_CARD
              }
              startIcon={CreditCardSvg}
              to={SettingsRoutes.pretaxCardList()}
            />
          )}
        </StyledMenu>
      </MobileContainer>
      <DesktopContainer>
        <AccountContainer>
          <SideNavigationBar header="Account settings">
            <SideLink
              key="Profile"
              route={SettingsRoutes.profile()}
              startIcon={<StyledUserIcon />}
            >
              Profile
            </SideLink>
            {show_dependent && (
              <SideLink
                key="Dependents"
                route={SettingsRoutes.dependents()}
                startIcon={<StyledUserPlusIcon />}
              >
                Dependents
              </SideLink>
            )}
            <SideLink
              key="Notifications"
              route={SettingsRoutes.notifications()}
              startIcon={<StyledNotificationIcon />}
            >
              Notifications
            </SideLink>
            <SideLink
              key="Linked accounts"
              active={
                location.pathname.includes('bank-accounts') ||
                location.pathname.includes('payment')
              }
              route={SettingsRoutes.bankAccounts()}
              startIcon={<StyledBankIcon />}
            >
              Linked accounts
            </SideLink>
          </SideNavigationBar>
          {showSecurity && (
            <SideNavigationBar>
              <SideLink
                key="Security"
                startIcon={<StyledLockIcon />}
                route={SettingsRoutes.security()}
              >
                Security
              </SideLink>
            </SideNavigationBar>
          )}
          {showIntegration && (
            <SideNavigationBar>
              <SideLink
                key="Integrations"
                startIcon={<StyledDashboardIcon />}
                route={SettingsRoutes.integrations()}
              >
                Integrations
              </SideLink>
            </SideNavigationBar>
          )}
          {showFormaCards && (
            <SideNavigationBar>
              <SideLink
                key={CardTabName.PRETAX_FORMA_CARD}
                startIcon={<StyledCreditCardIcon />}
                route={SettingsRoutes.formaCard()}
              >
                {CardTabName.PRETAX_FORMA_CARD}
              </SideLink>
            </SideNavigationBar>
          )}
          {showPostTaxCards && (
            <SideNavigationBar>
              <SideLink
                key={CardTabName.FORMA_CARD}
                startIcon={<StyledCreditCardIcon />}
                route={SettingsRoutes.posttaxCardList()}
              >
                {CardTabName.FORMA_CARD}
              </SideLink>
            </SideNavigationBar>
          )}
          {hasAlegeus && (
            <SideNavigationBar>
              <SideLink
                key={CardTabName.WCS_HSA_CARD}
                startIcon={<StyledCreditCardIcon />}
                route={SettingsRoutes.pretaxCardList()}
              >
                {hasAlegeusHsaAccount
                  ? CardTabName.WCS_HSA_CARD
                  : CardTabName.BENEFITS_CARD}
              </SideLink>
            </SideNavigationBar>
          )}
        </AccountContainer>
      </DesktopContainer>
    </Container>
  );
};

export default SettingsFilter;
